"use client";

import Image from "next/image";
import { PortableText } from "@portabletext/react";
import { PortableTextBlock } from "sanity";
import { GROQButton } from "@/sanity/lib/definitions";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { CTAButton } from "@/shadcn/ui/button";
import { FadeIn } from "@/components/global/utils/FadeIn";
import {
  IMasterClassCard,
  MasterClassIcon,
} from "@/sanity/pages/PageCourseGalleryTemplate";
import { PipeChip, PipeMeter } from "./_shared";

import iconTrophy from "@/public/courseGallery/icon-trophy.svg";
import iconAnalytics from "@/public/courseGallery/icon-analytics.svg";
import iconGroup from "@/public/courseGallery/icon-group.svg";
import iconSchool from "@/public/courseGallery/icon-school.svg";
import iconLaptop from "@/public/courseGallery/icon-laptop.svg";
import iconVerified from "@/public/courseGallery/icon-verified.svg";
import { TITLE_TRIM } from "@/components/global/sections/community/_shared";

// Material Symbols exported from Figma (3780:19276 → 3780:19311).
const ICONS: Record<MasterClassIcon, any> = {
  trophy: iconTrophy,
  analytics: iconAnalytics,
  group: iconGroup,
  school: iconSchool,
  laptop: iconLaptop,
  verified: iconVerified,
};

interface MasterClassSectionProps {
  id?: string;
  chip?: string;
  title?: PortableTextBlock[];
  plainTitle?: string;
  body?: string | PortableTextBlock[];
  cta?: GROQButton;
  cards?: IMasterClassCard[];
  featureCards?: StickyFeatureCard[];
  variant?: "courseGallery" | "community";
  /**
   * Show the community variant's CTA below the cards on mobile, full width.
   * The course-gallery variant always shows it there.
   */
  mobileCta?: boolean;
  /**
   * Centre the copy column (chip, title, body) below `lg`, the way the
   * course-gallery variant already does. Opt-in for the community variant,
   * which is left-aligned everywhere by default: the community page's design
   * review asked for a centred mobile header, the leaderboard's did not.
   */
  mobileCentered?: boolean;
  /**
   * Step the sub-`lg` headline up to 4.5rem (36px at the 435px mobile frame),
   * from the 3.75rem this section uses elsewhere. Same design review.
   */
  largeMobileTitle?: boolean;
  /** Size the desktop section to its cards instead of the community min-height. */
  autoHeight?: boolean;
  /** Tighter chip -> title -> body gaps, as the leaderboard's design review asked. */
  compactHeader?: boolean;
  trimTitle?: boolean;
}

export interface StickyFeatureCard {
  icon?: string;
  title?: string | PortableTextBlock[];
  description?: string | PortableTextBlock[];
}

// White section — Figma 3780:19257. The copy column sticks while the card
// column scrolls past it; `items-start` on the row is what lets `sticky` work.
export default function MasterClassSection({
  id,
  chip,
  title,
  plainTitle,
  body,
  cta,
  cards,
  featureCards,
  variant = "courseGallery",
  mobileCta = false,
  mobileCentered = false,
  largeMobileTitle = false,
  autoHeight = false,
  compactHeader = false,
  trimTitle = false,
}: MasterClassSectionProps) {
  const items = cards ?? [];
  const isCommunity = variant === "community";
  const visibleItems = isCommunity ? (featureCards ?? []) : items;
  // `.richtext` centres by default, so the modifier is always spelled out:
  // community is left-aligned everywhere, course-gallery centres on mobile —
  // and `mobileCentered` opts a community section into that same mobile
  // centring without touching the pages that did not ask for it.
  const centreOnMobile = !isCommunity || mobileCentered;
  const copyItems = centreOnMobile ? "items-center lg:items-start" : "items-start";
  const copyMx = centreOnMobile ? "mx-auto lg:mx-0" : "mx-0";
  const copyRich = centreOnMobile
    ? "richtext-center lg:richtext-start"
    : "richtext-start";
  const communityPage = isCommunity && !compactHeader;
  const titleSize = communityPage
    ? "prose-p:text-[4rem] prose-headings:text-[4rem]"
    : largeMobileTitle
      ? "prose-p:text-[4.5rem] prose-headings:text-[4.5rem]"
      : "prose-p:text-[3.75rem] prose-headings:text-[3.75rem]";
  const bodyWidth = communityPage
    ? "w-[42.5rem] prose-p:text-[2.25rem] lg:w-[54.875rem]"
    : "lg:w-[54.875rem]";
  const communityHeight = autoHeight ? "" : "lg:min-h-[165.125rem]";
  const bodyGap = compactHeader
    ? "gap-[1.625rem] lg:gap-[1.125rem]"
    : isCommunity
      ? "gap-[3rem]"
      : "gap-[1rem] lg:gap-[3rem]";
  const titleGap = compactHeader
    ? "gap-[2.5rem] lg:gap-[3.125rem]"
    : isCommunity
      ? "gap-[4rem]"
      : "gap-[2.5rem] lg:gap-[4rem]";

  return (
    <section
      id={id}
      data-navbar-theme="light"
      className={`flex w-full justify-center bg-white px-[2rem] pt-[8rem] pb-[8rem] lg:px-[45rem] lg:pt-[13.75rem] lg:pb-[11.25rem] ${
        isCommunity ? `text-black ${communityHeight}` : ""
      }`}
    >
      <div className="flex w-[min(150rem,100%)] flex-col items-start gap-[5rem] lg:flex-row lg:gap-[14rem]">
        <div className="flex w-full shrink-0 flex-col items-start gap-[3rem] lg:sticky lg:top-[12rem] lg:w-[74.25rem] lg:gap-[4rem]">
          <div className={`flex w-full flex-col ${bodyGap} ${copyItems}`}>
            <div className={`flex flex-col ${titleGap} ${copyMx} ${copyItems}`}>
              {chip && (
                <FadeIn delay={0}>
                  <PipeChip className={copyMx} color="#000000" hideRight>
                    {chip}
                  </PipeChip>
                </FadeIn>
              )}
              {title && (
                <FadeIn
                  delay={0.1}
                  className={`richtext ${copyRich} ${titleSize} ${trimTitle ? TITLE_TRIM : ""} max-w-full lg:w-[69rem] prose-p:m-0 prose-p:font-news prose-p:leading-[1.1] prose-p:tracking-[-0.15rem] prose-p:text-black/90 lg:prose-p:text-[6rem] lg:prose-p:tracking-[-0.25rem] prose-em:font-news prose-em:italic prose-headings:m-0 prose-headings:font-news prose-headings:leading-[1.1] prose-headings:tracking-[-0.15rem] prose-headings:text-black/90 lg:prose-headings:text-[6rem] lg:prose-headings:tracking-[-0.25rem]`}
                >
                  <PortableText value={title} components={richTextComponent} />
                </FadeIn>
              )}
              {plainTitle && (
                <FadeIn delay={0.1}>
                  <h2 className="m-0 w-[49.625rem] max-w-full whitespace-pre-line font-news text-[4rem] leading-[1.1] font-normal tracking-[-0.25rem] text-black/90 lg:w-[69rem] lg:text-[6rem]">
                    {plainTitle}
                  </h2>
                </FadeIn>
              )}
            </div>

            {body && (
              <FadeIn delay={0.15}>
                {Array.isArray(body) ? (
                  <div className={`richtext ${isCommunity ? copyRich : ""} ${bodyWidth} m-0 max-w-full font-geist text-[2rem] leading-[1.5] text-black opacity-60 prose-p:m-0 prose-p:text-inherit lg:text-[2.25rem]`}>
                    <PortableText value={body} components={richTextComponent} />
                  </div>
                ) : (
                  <p className={`m-0 max-w-full font-geist text-[2rem] leading-[1.5] text-black opacity-60 lg:w-[54.875rem] lg:text-[2.25rem] ${mobileCentered ? "text-center lg:text-start" : ""}`}>
                    {body}
                  </p>
                )}
              </FadeIn>
            )}
          </div>

          {(!isCommunity || cta?.cta) && (
            <FadeIn delay={0.2} className="hidden w-full lg:block lg:w-fit">
              <CTAButton data={cta ?? {}} />
            </FadeIn>
          )}
        </div>

        {visibleItems.length > 0 && (
          <div className="flex w-full flex-col items-start gap-[1.125rem] lg:w-[61rem]">
            {visibleItems.map((card, i) => {
              const icon = isCommunity
                ? (card as StickyFeatureCard).icon
                : (card as IMasterClassCard).icon
                  ? ICONS[(card as IMasterClassCard).icon!]
                  : null;
              const cardTitle = isCommunity
                ? (card as StickyFeatureCard).title
                : undefined;
              const cardDescription = isCommunity
                ? (card as StickyFeatureCard).description
                : (card as IMasterClassCard).text;

              return (
                <FadeIn
                  key={i}
                  delay={(i % 3) * 0.08}
                  className={`flex w-full flex-col items-start rounded-[1rem] bg-black/[0.02] p-[2.5rem] shadow-[inset_0px_0.5rem_3.3375rem_0px_rgba(0,0,0,0.04)] lg:p-[3.5rem] ${
                    isCommunity ? "" : "h-[22rem] lg:h-[25.25rem]"
                  }`}
                >
                  <div
                    className={`flex h-full w-full flex-col ${
                      isCommunity ? "gap-[12.5rem]" : "justify-between"
                    }`}
                  >
                    <div className="flex w-full items-center justify-between">
                      <div className="relative h-[4.5rem] w-[4.5rem] shrink-0 lg:h-[5rem] lg:w-[5rem]">
                        {icon && (
                          <Image
                            src={icon}
                            alt=""
                            aria-hidden
                            width={40}
                            height={40}
                            className="!h-full !w-full"
                          />
                        )}
                      </div>
                      <PipeMeter filled={i + 1} total={visibleItems.length} />
                    </div>

                    {isCommunity && (cardTitle || cardDescription) ? (
                      <div className="flex w-full flex-col items-start gap-[2rem] font-geist text-black">
                        {Array.isArray(cardTitle) ? (
                          <div className="richtext richtext-start prose-p:m-0 prose-p:font-geist prose-p:text-[2.75rem] prose-p:leading-[1.5] prose-p:font-normal prose-p:text-black prose-headings:m-0 prose-headings:font-geist prose-headings:text-[2.75rem] prose-headings:leading-[1.5] prose-headings:font-normal prose-headings:text-black">
                            <PortableText value={cardTitle} components={richTextComponent} />
                          </div>
                        ) : cardTitle ? (
                          <h3 className="m-0 font-geist text-[2.75rem] leading-[1.5] font-normal">
                            {cardTitle}
                          </h3>
                        ) : null}
                        {Array.isArray(cardDescription) ? (
                          <div className="richtext richtext-start [text-wrap:pretty] text-[2.25rem] leading-[1.5] text-black/60 prose-p:m-0 prose-p:text-[2.25rem] prose-p:leading-[1.5] prose-p:text-inherit">
                            <PortableText value={cardDescription} components={richTextComponent} />
                          </div>
                        ) : cardDescription ? (
                          <p className="m-0 text-[2.25rem] leading-[1.5] text-black/60">
                            {cardDescription}
                          </p>
                        ) : null}
                      </div>
                    ) : Array.isArray(cardDescription) ? (
                      <div className="richtext font-geist text-[2rem] leading-[1.5] text-black opacity-60 prose-p:m-0 prose-p:text-inherit lg:max-w-[34rem] lg:text-[2.25rem]">
                        <PortableText value={cardDescription} components={richTextComponent} />
                      </div>
                    ) : cardDescription ? (
                      <p className="m-0 font-geist text-[2rem] leading-[1.5] text-black opacity-60 lg:max-w-[34rem] lg:text-[2.25rem]">
                        {cardDescription}
                      </p>
                    ) : null}
                  </div>
                </FadeIn>
              );
            })}
          </div>
        )}

        {(!isCommunity || (mobileCta && cta?.cta)) && (
          <FadeIn delay={0.2} className="w-full lg:hidden lg:w-fit">
            <CTAButton data={cta ?? {}} />
          </FadeIn>
        )}
      </div>
    </section>
  );
}
